import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NodeApi, NodeRendererProps, Tree, TreeApi } from 'react-arborist'
import './folders.css'
import { useWindowDimensions } from '../hooks/useWindowDimensions'
import { ContextMenu, ContextMenuState } from '../ContextMenu'
import { OpenMap } from 'react-arborist/dist/module/state/open-slice'
import { IEditorNode } from '../Services/ServiceEditor'
import { GUID } from '../Services/ServiceTypes'
import { useElementDimensions } from '../hooks/useElementDimensions'
import { ViewStyle } from './mapEditorNodesToFolderNodes'

export type FolderNode = {
  id: string
  name: string
  children?: FolderNode[]
  parentId?: string
  isOpen?: boolean
  isSelected?: boolean
  isLocked?: boolean
  lastEditTextTimestamp?: Date
  tags?: string[]
  numberOfTexts: number
  path: string
}

export const toggleFolderNode = (nodes: FolderNode[], targetId: string): FolderNode[] => {
  return nodes.map(node => {
    if (node.id === targetId) {
      return {
        ...node,
        isOpen: !node.isOpen,
      }
    }

    if (node.children?.length) {
      return {
        ...node,
        children: toggleFolderNode(node.children, targetId),
      }
    }

    return node
  })
}

export const selectFolderNode = (nodes: FolderNode[], targetId: string | null): FolderNode[] => {
  return nodes.map(node => {
    if (node.children?.length) {
      return {
        ...node,
        isSelected: node.id === targetId,
        children: selectFolderNode(node.children, targetId),
      }
    }

    return {
      ...node,
      isSelected: node.id === targetId,
    }
  })
}

export const getNodeById = (nodes: FolderNode[], nodeId: string): FolderNode | undefined => {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node
    }

    if (node.children) {
      const childNode = getNodeById(node.children, nodeId)
      if (childNode) {
        return childNode
      }
    }
  }

  return undefined
}

export const getAPINodeById = (nodes: IEditorNode[] | undefined, nodeId: string): IEditorNode | undefined => {
  if (!nodes) return undefined

  for (const node of nodes) {
    if (node.Id === nodeId) {
      return node
    }
  }

  return undefined
}

interface IFolderExplorerProps {
  defaultSelectedNodeId: GUID | null
  nodes: FolderNode[]
  nodeIdChanged: GUID | undefined
  onSelectFolder?: (nodeId: string) => void
  onSelectText?: (nodeId: string) => void
  onRefresh?: () => void
  onCreateFolder?: (parentId: string | null) => void
  onCreateText?: (parentId: string | null) => void
  onToggleNode?: (nodeId: string) => void
  onRenameNode?: (nodeId: string) => void
  onRemoveNode?: (nodeId: string) => void
  onMoveNode?: (nodeId: string, newParentId: string | undefined) => void
  marginBottom?: number
  canNavigate?: boolean
  canCreateNodes?: boolean
  viewStyle?: ViewStyle
}

const rekMapNodesToInitialOpenState = (nodes: FolderNode[]) => {
  //
  const openNodes = [] as FolderNode[]
  for (const node of nodes) {
    if (node.isOpen) {
      openNodes.push(node)
    }
    if (node.children) {
      for (const openNode of rekMapNodesToInitialOpenState(node.children ?? [])) {
        openNodes.push(openNode)
      }
    }
  }
  return openNodes
}

const mapNodesToInitialOpenState = (rootNodes: FolderNode[]) => {
  const initialOpenState: OpenMap = {}
  const openNodes = rekMapNodesToInitialOpenState(rootNodes)
  for (const openNode of openNodes) {
    initialOpenState[openNode.id] = true
  }
  return initialOpenState
}

const FolderExplorer = ({
  defaultSelectedNodeId,
  nodes,
  nodeIdChanged,
  onSelectFolder,
  onSelectText,
  onRefresh,
  onCreateFolder,
  onCreateText,
  onRenameNode,
  onRemoveNode,
  onToggleNode,
  onMoveNode,
  marginBottom,
  canNavigate,
  canCreateNodes,
  viewStyle,
}: IFolderExplorerProps) => {
  const { width, height } = useWindowDimensions()

  const data = nodes
  const lastSelectedIdRef = useRef<string | null>(null)
  const onSelect = useCallback(
    (nodes: NodeApi<FolderNode>[]) => {
      if (nodes.length !== 1) return
      const nextId = nodes[0]?.id ?? null
      const prevId = lastSelectedIdRef.current

      // Første gang: bare acceptér
      if (!prevId) {
        lastSelectedIdRef.current = nextId
      }
      if (nextId === prevId) {
        return
      }
      // Hvis vi ikke må navigere, så hop tilbage til forrige
      if (!canNavigate) {
        // evt: vis toast/modal "Gem først"
        // showUnsavedModal();

        if (treeRef.current && prevId) {
          // rulle tilbage (næste tick undgår "fight" med intern state)
          queueMicrotask(() => {
            treeRef.current?.select(prevId)
            treeRef.current?.focus(prevId)
          })
        }
        return
      } else {
        const node = nodes[0]
        if (node.children !== null) {
          onSelectFolder?.(node.id)
        } else {
          onSelectText?.(node.id)
        }
      }

      // Ellers acceptér skift
      lastSelectedIdRef.current = nextId
    },
    [canNavigate, onSelectText],
  )

  const onMove = useCallback(
    async (args: { dragIds: string[]; parentId: string | null; index: number }) => {
      //console.log(args)
      // TODO: PATCH /nodes/move { dragIds, parentId, index }
      if (args.dragIds.length !== 1 || !canNavigate) return
      onMoveNode?.(args.dragIds[0], args.parentId ?? undefined)
    },
    [nodes, canNavigate],
  )

  // const onSelect = useCallback(
  //   (nodes: NodeApi<any>[]) => {
  //     if (nodes.length !== 1) return
  //     const node = nodes[0]
  //     if (node.children !== null) {
  //       onSelectFolder?.(node.id)
  //     } else {
  //       onSelectText?.(node.id)
  //     }
  //   },
  //   [onSelectText],
  // )

  const [menu, setMenu] = useState<ContextMenuState | null>(null)
  const [menuForNode, setMenuForNode] = useState<FolderNode>()

  const closeMenu = useCallback(() => setMenu(null), [])

  const onAction = useCallback(
    async (ctx: ContextMenuState, action: any) => {
      closeMenu()

      const parentId = ctx.target === 'root' ? null : ctx.nodeKind === 'folder' ? ctx.nodeId : null // (typisk: kan ikke oprette under en fil)

      // console.log(`action.type = ${action.type}`)

      switch (action.type) {
        case 'refresh':
          onRefresh?.()
          break
        case 'create-folder':
          onCreateFolder?.(parentId)
          // await api.createFolder({ parentId })
          break
        case 'create-note':
          onCreateText?.(parentId)
          // await api.createTextDoc({ parentId })
          break
        case 'rename':
          if (ctx.target === 'node') {
            onRenameNode?.(ctx.nodeId)
            // await api.deleteNode({ id: ctx.nodeId })
          }
          break
        case 'delete':
          if (ctx.target === 'node') {
            onRemoveNode?.(ctx.nodeId)
            // await api.deleteNode({ id: ctx.nodeId })
          }
          break
      }
    },
    [closeMenu, onCreateFolder, onRefresh, onRenameNode, onRemoveNode, onCreateText],
  )

  const treeKey = data?.length ? 'loaded' : 'empty'
  const treeRef = React.useRef<TreeApi<FolderNode> | null>(null)

  useEffect(() => {
    if (!data?.length) return
    if (!defaultSelectedNodeId) return
    //if (didSelect.current) return

    // vent til efter mount/render hvor ref er sat
    queueMicrotask(() => {
      treeRef.current?.select(defaultSelectedNodeId)
      onSelectText?.(defaultSelectedNodeId)
      // setTimeout(() => {
      //   someInputRef.current?.focus()
      // }, 0)
      //treeRef.current?.focus(defaultSelectedNodeId) // hvis du vil have focus
      // treeRef.current?.scrollTo(defaultId); // hvis din version har den
    })

    //didSelect.current = true
  }, [!!data, defaultSelectedNodeId, treeKey])

  const { ref: containerRef, dimensions: containerDimensions } = useElementDimensions<HTMLDivElement>()

  return (
    <div
      ref={containerRef}
      className="tree-canvas"
      onContextMenu={e => {
        // Hvis du højreklikker direkte på canvas (ikke en node),
        // så åbner vi root-menu

        //console.log(`onContextMenu`, e.clientX)
        e.preventDefault()
        setMenu({
          isOpen: true,
          x: e.clientX,
          y: e.clientY,
          target: 'root',
        })
      }}
    >
      <Tree
        ref={treeRef}
        key={treeKey}
        data={data}
        // onCreate={onCreate}
        // onRename={onRename}
        onMove={onMove}
        // onDelete={onDelete}
        onSelect={onSelect}
        //   onClick={x => {
        //     console.log(x)
        //   }}
        height={height - (marginBottom ?? 0)}
        width={containerDimensions.width * 0.98}
        rowHeight={32}
        indent={10}
        openByDefault={false}
        initialOpenState={mapNodesToInitialOpenState(nodes)}
        selectionFollowsFocus={true}
        disableMultiSelection={true}
        disableDrag={d => !!d.isLocked || !canNavigate}
        className="folderTree"
      >
        {props => (
          <Node
            {...props}
            openContextMenu={s => {
              //console.log(`openContextMenu`, s.x)
              setMenu(s)
              setMenuForNode(props.node.data)
            }}
            toggleNode={onToggleNode}
            nodeIdChanged={nodeIdChanged}
            viewStyle={viewStyle}
            nodes={nodes}
          />
        )}
      </Tree>
      <ContextMenu state={menu} node={menuForNode} onClose={closeMenu} onAction={onAction} canCreateNodes={canCreateNodes} />
    </div>
  )
}

export default FolderExplorer

export const FolderNodeUI = (node: FolderNode, nodes?: FolderNode[], nodeIdChanged?: string, viewStyle?: ViewStyle) => {
  return (
    <>
      <span className="icon">{node.children !== undefined ? '📁' : '📄'}</span>
      <span
        className="label"
        style={{ flex: 1, fontWeight: node.isSelected ? 'bold' : undefined, color: nodeIdChanged ? (nodeIdChanged === node.id ? 'red' : 'gray') : 'black' }}
      >
        {node.name}

        {/* Lock badge */}
        <span className={['lock-icon', node.isLocked ? 'locked' : 'unlocked'].join(' ')} title={node.isLocked ? 'Låst' : 'Ulåst'} style={{ marginLeft: 8 }}>
          {node.isLocked ? '🔒' : ''}
        </span>

        {node.children !== undefined && node.numberOfTexts > 0 && <span className="itemcount">{node.numberOfTexts}</span>}
        {viewStyle === 'byFolder' &&
          node.tags?.map((tag, tagIdx) => {
            return (
              <span className={`${tagIdx === 0 ? 'tagfirst' : 'tag'}`} key={`${node.id}tag${tag}`}>
                {tag}
              </span>
            )
          })}
        {viewStyle === 'sortedDesc' && <span className="tagfirst">{node.path}</span>}
      </span>
      <span className="timestamp">({node.lastEditTextTimestamp?.HHMM_ddmmyyyy_ui3()})</span>
    </>
  )
}

function Node({
  node,
  nodes,
  style,
  dragHandle,
  openContextMenu,
  toggleNode,
  nodeIdChanged,
  viewStyle,
}: NodeRendererProps<FolderNode> & {
  nodes: FolderNode[]
  openContextMenu: (s: ContextMenuState) => void
  toggleNode?: (nodeId: string) => void
  nodeIdChanged?: string
  viewStyle?: ViewStyle
}) {
  const dropHover = node.willReceiveDrop && node.isInternal

  return (
    <div
      ref={dragHandle}
      style={style}
      className={['tree-node', node.isSelected && 'selected', dropHover && 'drop-hover'].filter(Boolean).join(' ')}
      onContextMenu={e => {
        e.preventDefault()
        e.stopPropagation()

        openContextMenu({
          isOpen: true,
          x: e.clientX,
          y: e.clientY,
          target: 'node',
          nodeId: node.id,
          nodeKind: node.isInternal ? 'folder' : 'text',
        })
      }}
    >
      <span
        className="caret"
        onClick={e => {
          e.stopPropagation()
          if (node.isInternal) {
            node.toggle()
            toggleNode?.((node.data as FolderNode).id)
          } else {
          }
        }}
      >
        {node.isInternal ? (node.isOpen ? '▼' : '▶') : ''}
      </span>
      {FolderNodeUI(node.data, nodes, nodeIdChanged, viewStyle)}
    </div>
  )
}
